import type { EventBootstrapResponse, PublicLocation } from '@irace/shared';
import { useCallback, useMemo, useRef, useState } from 'react';
import { EventBanner } from '../components/EventBanner.tsx';
import { EventInfoPanel } from '../components/EventInfoPanel.tsx';
import { PoweredByFooter } from '../components/PoweredByFooter.tsx';
import { COPY } from '../copy/vi.ts';
import { FormFieldInput } from './FormFieldInput.tsx';
import { LocationPicker } from './LocationPicker.tsx';
import { chooseLocationControl, initialLocation, type LocationIntent } from './locationIntent.ts';
import {
  LOCATION_FIELD_KEY,
  validateForm,
  validateSingleField,
  type FormValues,
} from './validation.ts';

export interface CheckinSubmission {
  locationId: string;
  /** Snapshotted with the submission so a pending record can name its location. */
  locationName: string;
  values: FormValues;
}

/**
 * M02 (§7, §8, §9, §28.2, §28.4) — the check-in form.
 *
 * Fields come from the deployed event config, including which are required
 * and their help text: §28.4 is explicit that email being required is a
 * config fact, not a hard-coded one, so a future event that does not issue
 * certificates can drop it without a code change.
 */
export function CheckinForm({
  bootstrap,
  intent,
  submitting,
  onSubmit,
  onTouched,
  children,
}: {
  bootstrap: EventBootstrapResponse;
  /** What the URL said about where the participant is standing. */
  intent: LocationIntent;
  submitting?: boolean;
  onSubmit: (submission: CheckinSubmission) => void;
  /**
   * Fired on the participant's first interaction of any kind.
   *
   * The values live in this component's state, so anything that unmounts it
   * loses them. The page uses this to know that it must not swap the form out
   * for the check-out resume screen — a decision that arrives asynchronously
   * and can land mid-sentence.
   */
  onTouched?: () => void;
  /** Turnstile widget and submission state UI, injected by the page. */
  children?: React.ReactNode;
}) {
  const { event, locations, form } = bootstrap;

  // A QR printed for one venue names that venue, and the participant is not
  // asked again — until they say the QR was wrong, which is what `unlocked`
  // records. It only ever goes one way, so a participant who has taken over
  // the choice keeps it for the rest of the visit.
  const [unlocked, setUnlocked] = useState(false);

  // How many real locations the event has and what the URL asked for, together
  // (§ location visibility). Whichever control this yields, a location is
  // still always submitted.
  const locationControl = useMemo(
    () => chooseLocationControl(locations, intent, unlocked),
    [locations, intent, unlocked],
  );

  const [selectedLocation, setSelectedLocation] = useState<PublicLocation | null>(() =>
    initialLocation(locations, intent),
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [values, setValues] = useState<FormValues>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const inputRefs = useRef(new Map<string, HTMLInputElement>());
  const locationButtonRef = useRef<HTMLButtonElement>(null);
  const fields = useMemo(() => form.fields, [form.fields]);

  const registerInput = useCallback(
    (key: string) => (element: HTMLInputElement | null) => {
      if (element) inputRefs.current.set(key, element);
      else inputRefs.current.delete(key);
    },
    [],
  );

  /** §9 — clear a field's error as soon as the participant edits it. */
  const setValue = useCallback(
    (key: string, value: string) => {
      onTouched?.();
      setValues((current) => ({ ...current, [key]: value }));
      setErrors((current) => {
        if (!(key in current)) return current;
        const { [key]: _cleared, ...rest } = current;
        return rest;
      });
    },
    [onTouched],
  );

  const handleSubmit = (formEvent: React.FormEvent) => {
    formEvent.preventDefault();
    if (submitting) return;

    const issues = validateForm(fields, values, selectedLocation?.id ?? null);
    setErrors(Object.fromEntries(issues.map((issue) => [issue.key, issue.message])));

    if (issues.length > 0) {
      // §9 — scroll the first invalid control into view and focus it, rather
      // than leaving the participant to hunt for the red text.
      const [first] = issues;
      const target =
        first?.key === LOCATION_FIELD_KEY
          ? locationButtonRef.current
          : inputRefs.current.get(first?.key ?? '');
      target?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      target?.focus({ preventScroll: true });
      return;
    }

    if (!selectedLocation) return;
    onSubmit({ locationId: selectedLocation.id, locationName: selectedLocation.name, values });
  };

  const locationError = errors[LOCATION_FIELD_KEY];

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-[480px] flex-col bg-bg">
      <EventBanner event={event} />

      <header className="flex flex-col items-center border-b border-[#e4e9f0] bg-surface px-5 pt-4 pb-5">
        <p className="text-2xl font-extrabold tracking-wide">{COPY.form.heading}</p>
        <h1 className="text-center text-[15px] font-semibold text-brand">{event.name}</h1>
      </header>

      <form noValidate onSubmit={handleSubmit} className="flex flex-col gap-4.5 px-5 pt-6 pb-7">
        {locationControl.kind === 'pick' ? (
          <div className="flex flex-col gap-1.5">
            {/* A URL that named a venue this event does not serve. Said once,
                here, rather than leaving an empty required field the
                participant was told they would never see. */}
            {locationControl.notFound ? (
              <p role="status" className="text-xs font-semibold text-danger">
                <span aria-hidden="true">⚠ </span>
                {COPY.form.locationNotFound}
              </p>
            ) : null}
            <span id="location-label" className="text-sm font-semibold">
              {COPY.form.locationLabel}
              <span className="text-danger" aria-hidden="true">
                {' '}
                *
              </span>
            </span>
            <button
              type="button"
              ref={locationButtonRef}
              onClick={() => {
                onTouched?.();
                setPickerOpen(true);
              }}
              aria-labelledby="location-label"
              aria-haspopup="dialog"
              aria-invalid={locationError ? true : undefined}
              aria-describedby={locationError ? 'location-error' : undefined}
              className={`flex h-13 items-center justify-between rounded-control border-[1.5px] bg-surface px-4 text-base ${
                locationError ? 'border-danger' : 'border-border'
              } ${selectedLocation ? 'text-text' : 'text-[#98a4b5]'}`}
            >
              {selectedLocation ? selectedLocation.name : COPY.form.locationPlaceholder}
              <span aria-hidden="true" className="text-[13px] text-text-muted">
                ▾
              </span>
            </button>
            {locationError ? (
              <p id="location-error" className="text-xs font-semibold text-danger">
                <span aria-hidden="true">⚠ </span>
                {locationError}
              </p>
            ) : null}
          </div>
        ) : locationControl.kind === 'locked' ? (
          // A QR taped to one venue's door. The venue is stated rather than
          // asked for, with a way back to the picker for the participant who
          // scanned the wrong door.
          <div className="flex flex-col gap-1.5 rounded-control border-[1.5px] border-border bg-surface px-4 py-3">
            <p className="text-sm">
              <span className="font-semibold">{COPY.form.locationLockedLabel}: </span>
              {locationControl.location.name}
            </p>
            <button
              type="button"
              onClick={() => {
                onTouched?.();
                setUnlocked(true);
                setPickerOpen(true);
              }}
              className="self-start text-xs font-semibold text-brand underline"
            >
              {COPY.form.locationChange}
            </button>
          </div>
        ) : locationControl.kind === 'fixed' ? (
          // One venue: shown, not asked. The participant still sees which one,
          // so a QR code scanned at the wrong event is obvious.
          <p className="text-sm">
            <span className="font-semibold">{COPY.form.locationFixedLabel}: </span>
            {locationControl.location.name}
          </p>
        ) : null}

        {fields.map((field) => (
          <FormFieldInput
            key={field.key}
            field={field}
            value={values[field.key] ?? ''}
            error={errors[field.key] ?? null}
            inputRef={registerInput(field.key)}
            onChange={(value) => {
              setValue(field.key, value);
            }}
            onBlur={() => {
              const message = validateSingleField(field, values[field.key]);
              setErrors((current) => (message ? { ...current, [field.key]: message } : current));
            }}
          />
        ))}

        {children}

        <button
          type="submit"
          disabled={submitting}
          className="h-14 rounded-control bg-brand text-[17px] font-extrabold tracking-wide text-brand-contrast shadow-[0_2px_6px_rgb(0_102_255_/_0.35)] disabled:opacity-60"
        >
          {submitting ? COPY.form.submitting : COPY.form.submit}
        </button>

        <p className="text-center text-xs leading-relaxed text-text-muted">
          {COPY.form.privacy}
          <br />
          {COPY.form.support}
        </p>

        <EventInfoPanel event={event} organization={bootstrap.organization} />

        <PoweredByFooter organization={bootstrap.organization} />
      </form>

      {locationControl.kind === 'pick' ? (
        <LocationPicker
          locations={locations}
          selectedId={selectedLocation?.id ?? null}
          open={pickerOpen}
          onSelect={(location) => {
            onTouched?.();
            setSelectedLocation(location);
            setPickerOpen(false);
            setErrors((current) => {
              const { [LOCATION_FIELD_KEY]: _cleared, ...rest } = current;
              return rest;
            });
            locationButtonRef.current?.focus();
          }}
          onClose={() => {
            setPickerOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}
